"use client";

import NextImage from "next/image";

import { cn } from "@/lib/utils";
import { IReplayOfferCard, PageReplaySchema } from "@/sanity/pages/PageReplayTemplate";
import { CAP_TRIM, EventFooter, TicketCTA, useCountdownUnits } from "@/components/pageComponents/event/EventShared";

import offerTile from "@/public/replay/offer-tile.png";
import check from "@/public/replay/check.svg";
import plus from "@/public/replay/plus.svg";
import gift from "@/public/replay/gift.svg";
import dottedArrow from "@/public/replay/dotted-arrow.svg";

/** One ticked "1 Konto" box (white) or the green free-account box. */
function OfferSlot({ label, free }: { label?: string; free?: boolean }) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-[1.5rem]",
        free
          ? "w-full bg-[#228e71] py-[2.625rem] text-white lg:h-[16rem] lg:w-[24.875rem] lg:pt-[2.75rem] lg:pb-[2rem]"
          : "h-[16rem] min-w-0 flex-1 border border-black/10 bg-white pt-[2.75rem] pb-[2rem] text-black/70",
      )}>
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-[0.375rem]",
          free
            ? "h-[4rem] w-[4rem] bg-white lg:h-[5rem] lg:w-[5rem]"
            : "h-[4rem] w-[4rem] bg-[#228e71] lg:h-[4.625rem] lg:w-[4.625rem]",
        )}>
        <NextImage
          src={free ? gift : check}
          alt=""
          aria-hidden
          className={free ? "h-[3rem] w-[3rem]" : "h-[2.625rem] w-[2.625rem]"}
        />
      </span>
      {label && (
        <span className="font-geist text-[2rem] leading-[1.3] font-normal whitespace-nowrap lg:text-[2.25rem]">
          {label}
        </span>
      )}
    </div>
  );
}

/** `wide` is the single-card layout: the card fills the 900px column (Figma 8650:1369). */
function OfferCard({ card, wide }: { card: IReplayOfferCard; wide?: boolean }) {
  const paidSlots = Math.max(0, Math.round(card.paidSlots ?? 0));

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-[2rem] border border-black/10 bg-white px-[1.5rem] pt-[1.5rem] pb-[2rem] lg:gap-[2.5rem] lg:p-[2.25rem]",
        wide ? "lg:w-full" : "lg:w-[84.625rem]",
      )}>
      {/* Offer visual on the dotted grey panel */}
      <div className="relative flex min-h-[37.5rem] flex-1 flex-col gap-[3.5rem] overflow-clip p-[2rem] lg:h-[45.5rem] lg:p-[3rem]">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[#f5f5f5]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.64]"
          style={{
            backgroundImage: `url(${offerTile.src})`,
            backgroundSize: "3.5rem 3.5rem",
            backgroundPosition: "top left",
          }}
        />

        {card.productLabel && (
          <p className="relative mb-0 w-fit bg-[#ffda74] px-[1.875rem] py-[1.25rem] font-geist text-[2.25rem] leading-[1.3] font-normal tracking-[-0.0675rem] whitespace-nowrap text-black lg:text-[2.5rem] lg:tracking-[-0.075rem]">
            {card.productLabel}
          </p>
        )}

        <div className="relative flex flex-1 flex-col gap-[2rem] lg:justify-between lg:gap-[2.5rem]">
          {card.title && (
            <p className="mb-0 font-geist text-[3.5rem] leading-[1.14] font-medium whitespace-pre-line text-black lg:text-[4rem] lg:leading-[1.3]">
              {card.title}
            </p>
          )}

          {/* Slots: paid boxes + plus + free box (row on desktop, stacked on mobile) */}
          <div className="flex w-full flex-col items-center gap-[1rem] lg:flex-row">
            <div className="flex w-full gap-[1rem] lg:contents">
              {Array.from({ length: paidSlots }, (_, i) => (
                <OfferSlot
                  key={i}
                  label={card.paidSlotLabel}
                />
              ))}
            </div>
            <NextImage
              src={plus}
              alt="+"
              className="h-[2.625rem] w-[2.625rem] shrink-0"
            />
            <OfferSlot
              label={card.freeSlotLabel}
              free
            />
          </div>
        </div>
      </div>

      {card.footnote && (
        <p className="mb-0 font-geist text-[2rem] leading-[1.5] font-normal text-black/70 lg:text-[2.25rem]">
          {card.footnote}
        </p>
      )}
    </div>
  );
}

/** Grey countdown box: digits, progress rule, uppercase end-date label (Figma 8610:1085). */
function ReplayCountdown({ label, target }: { label?: string; target?: string }) {
  const units = useCountdownUnits(target);

  return (
    <div className="flex w-full flex-col items-center justify-center gap-[1.87rem] rounded-[0.374rem] border-[0.08rem] border-black/10 bg-[#f5f5f5] px-[3.74rem] py-[1.87rem] lg:w-auto lg:min-w-[66.47rem] lg:gap-[2.5rem] lg:rounded-[0.5rem] lg:border-[0.11rem] lg:px-[5rem] lg:py-[2.5rem]">
      <div className="flex items-center gap-[2.06rem] pb-[0.75rem] text-center text-black lg:gap-[2.75rem] lg:pb-[1rem]">
        {units.map((unit, i) => (
          <div
            key={unit.label}
            className="contents">
            {i > 0 && (
              <p className="mb-0 font-sans text-[2.22rem] leading-none font-medium opacity-70 lg:text-[2.96rem]">:</p>
            )}
            <div className="flex flex-col items-center gap-[0.55rem] lg:gap-[0.74rem]">
              <p className="mb-0 font-geist text-[5rem] leading-[1.3] font-medium lg:text-[6rem]">{unit.value}</p>
              <p className="mb-0 font-geist text-[1.5rem] leading-[1.3] font-normal uppercase opacity-70 lg:text-[1.815rem]">
                {unit.label}
              </p>
            </div>
          </div>
        ))}
      </div>
      {/* Line bar: grey rule with the dark leading segment */}
      <div
        aria-hidden
        className="relative h-[0.25rem] w-full bg-[#989898]/40 lg:w-[56.25rem]">
        <span className="absolute inset-y-0 left-0 w-[29.75%] bg-[#202020]" />
      </div>
      {label && (
        <p className="mb-0 text-center font-geist text-[1.75rem] leading-[1.3] font-normal whitespace-nowrap uppercase opacity-70 lg:text-[2.07rem]">
          {label}
        </p>
      )}
    </div>
  );
}

/**
 * White offers section: badge + title, one or two offer cards, dotted arrow,
 * offer countdown + CTA, light footer. `s2CardsShown` picks the layout: two
 * 677px cards in a 1380px column (Figma 8610:2266) or a single card filling a
 * 900px column (Figma 8650:1359).
 */
export function ReplayOffers({ data }: { data: PageReplaySchema }) {
  const single = data.s2CardsShown === 1;
  const cards = (data.s2Cards ?? []).slice(0, single ? 1 : 2);
  const footerLinks = data.footerLinks ?? [];

  if (data.hideOffer !== false) {
    return (
      <EventFooter
        variant="light"
        copyright={data.footerCopyright}
        links={footerLinks}
      />
    );
  }

  return (
    <section
      id="angebot"
      className="flex w-full flex-col items-center gap-[10rem] bg-[#fbfbfb] pt-[10rem] lg:gap-[17.5rem] lg:pt-[18.75rem]">
      <div
        className={cn(
          "flex w-[min(49.875rem,calc(100%-3.875rem))] flex-col items-center gap-[3rem] lg:gap-[4rem]",
          single ? "lg:w-[112.5rem]" : "lg:w-[172.5rem]",
        )}>
        {/* ── Header + cards (+ mobile CTA) ───────────────────────────── */}
        <div className="flex w-full flex-col items-center gap-[2.25rem]">
          <div className="flex w-full flex-col items-center gap-[4rem] lg:gap-[6rem]">
            <div className="flex flex-col items-center gap-[2.75rem]">
              {data.s2BadgeText && (
                <div className="flex items-center gap-[1.25rem] border border-black/10 bg-black/[0.04] px-[2.5rem] py-[1.5rem]">
                  <span className="h-[0.75rem] w-[0.75rem] rounded-full bg-[#228e71]" />
                  <p
                    className={cn(
                      "mb-0 font-geist text-[1.75rem] leading-[1.146] font-medium whitespace-nowrap text-black uppercase",
                      CAP_TRIM,
                    )}>
                    {data.s2BadgeText}
                  </p>
                </div>
              )}
              {(data.s2TitleAccent || data.s2TitleRest) && (
                <h2
                  className={cn(
                    "mb-0 w-[42.5rem] text-center font-blacker text-[4.25rem] leading-[1.12] font-normal tracking-[-0.1275rem] text-black lg:w-[103.625rem] lg:text-[6rem] lg:tracking-[-0.18rem] [&_span]:font-blacker",
                    CAP_TRIM,
                  )}>
                  {data.s2TitleAccent && <span className="text-[#296e5a]">{data.s2TitleAccent} </span>}
                  {data.s2TitleRest}
                </h2>
              )}
            </div>

            {!!cards.length && (
              <div
                className={cn(
                  "flex w-full flex-col-reverse gap-[1.75rem] lg:flex-row lg:items-stretch lg:gap-[3.25rem]",
                  single ? "lg:w-full" : "lg:w-auto",
                )}>
                {cards.map((card, i) => (
                  <OfferCard
                    key={card._key ?? i}
                    card={card}
                    wide={single}
                  />
                ))}
              </div>
            )}
          </div>

          <TicketCTA
            data={data.s2Cta?.cta ? data.s2Cta : data.cta}
            className="w-full lg:hidden"
            textClassName="text-[2.25rem]"
          />
        </div>

        {/* ── Dotted arrow (Figma 8610:931) ────────────────────────────── */}
        <NextImage
          src={dottedArrow}
          alt=""
          aria-hidden
          className="h-[11.125rem] w-[4rem]"
        />

        {/* ── Offer countdown + CTA ────────────────────────────────────── */}
        <div className="flex w-full flex-col items-center gap-[2.25rem] lg:w-[72.2rem] lg:gap-[3rem]">
          <div className="flex w-full flex-col items-center gap-[3rem]">
            {data.s3Title && (
              <h2
                className={cn(
                  "mb-0 w-full text-center font-blacker text-[4rem] leading-[1.12] font-normal text-black",
                  CAP_TRIM,
                )}>
                {data.s3Title}
              </h2>
            )}
            <ReplayCountdown
              label={data.countdownLabel}
              target={data.countdownTarget}
            />
          </div>
          <TicketCTA
            data={data.s3Cta?.cta ? data.s3Cta : data.cta}
            className="w-full lg:w-[44.375rem]"
            textClassName="text-[2.25rem]"
          />
        </div>
      </div>

      <EventFooter
        variant="light"
        copyright={data.footerCopyright}
        links={footerLinks}
      />
    </section>
  );
}
